import { allowedISODateFormats, allowedISODateFormatsWithoutDay } from './constants.js';
import { DateTime } from 'luxon';

/**
 * Converts worktime from seconds to minutes and rounds to the nearest whole number
 * @param {number|null|undefined} value - The worktime value in seconds
 * @returns {string|number} Empty string if value is falsy, otherwise rounded minutes as number
 * @example
 * formatWorktimeInMinutes(120) // returns 2
 * formatWorktimeInMinutes(150) // returns 3 (rounded from 2.5)
 * formatWorktimeInMinutes(0) // returns ''
 * formatWorktimeInMinutes(null) // returns ''
 */
export function formatWorktimeInMinutes(value) {
	if (!value) {
		return '';
	}
	return Math.round(value / 60);
}

/**
 * Converts an array of qualification IDs to full qualification objects using a lookup function.
 * If the input is already an array of objects, it returns it as-is.
 *
 * @param {Array<string|Object>} qualifications - Array of qualification IDs or objects.
 * @param {Function} getQualificationById - Function that takes an ID and returns the qualification object.
 * @returns {Array<Object>} Array of qualification objects with at least `_id`, `name`, and `description`.
 *
 */
export function mapQualificationIdsToObjects(qualifications, getQualificationById) {
	const isArrayOfIds = Array.isArray(qualifications) && qualifications.length > 0 && typeof qualifications[0] === 'string';

	if (isArrayOfIds) {
		// Map each ID to a full qualification object (id, name, description)
		return qualifications.map((id) => {
			const qualification = getQualificationById(id);
			return {
				_id: id,
				name: qualification?.name || '',
				description: qualification?.description || '',
			};
		});
	}

	// Already full objects, return as-is
	return qualifications;
}

/**
 * Validate if a given string is in the correct 24-hour time format (HH:mm).
 *
 * The format must be:
 * - Hours: 00 to 23
 * - Minutes: 00 to 59
 *
 * @param {string} timeString - The time string to validate.
 * @returns {boolean} Returns true if the string is a valid time in HH:mm format, otherwise false.
 *
 * @example
 * isValidTimeFormat("09:30"); // true
 * isValidTimeFormat("23:59"); // true
 * isValidTimeFormat("24:00"); // false
 * isValidTimeFormat("9:00");  // false
 */
export function isValid24HourTime(timeString) {
	const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;
	return TIME_REGEX.test(timeString);
}

/**
 * Validates whether the duration between two DateTime objects
 * is within the allowed maximum hours.
 *
 * @param {DateTime} start - Start as a Luxon DateTime instance.
 * @param {DateTime} end - End as a Luxon DateTime instance.
 * @param {number} [maxHours=12] - Maximum allowed duration in hours.
 * @returns {boolean} True if duration is valid (> maxHours), otherwise false.
 */
export function isDurationWithinLimit(start, end, maxHours) {
	if (!start || !end) return true;

	const diffHours = end.diff(start, 'hours').hours;
	return diffHours > maxHours;
}

/**
 * Parse date
 *
 * @param {string} date - date to be parsed
 * @param {boolean} hideDay - flag to indicate if day should be hidden
 * @param {Array} localeFormatsWithoutDay - locale date formats where day is hidden
 * @param {Array} localeFormats - local day formats including day
 * @returns {luxon.DateTime|null} DateTime
 */
export function parseDate(date, hideDay, localeFormatsWithoutDay, localeFormats) {
	let parsedDate = null;

	//If hideDay -> Use L.yyyy
	if (hideDay) {
		const allowedFormatsWithoutDay = [...localeFormatsWithoutDay, ...allowedISODateFormatsWithoutDay];
		for (const format of allowedFormatsWithoutDay) {
			const result = DateTime.fromFormat(date, format);
			if (result.isValid) {
				parsedDate = result;
				break;
			}
		}
		return parsedDate;
	}

	const allowedFormats = [...localeFormats, ...allowedISODateFormats];

	for (const format of allowedFormats) {
		const result = DateTime.fromFormat(date, format);
		if (result.isValid) {
			parsedDate = result;
			break;
		}
	}

	return parsedDate;
}

/**
 * Compares two arrays for strict equality.
 * - Order matters
 * - Values must match exactly
 * - Handles null/undefined safely
 *
 * @param {Array} arr1
 * @param {Array} arr2
 * @returns {boolean} true if arrays are equal
 */
export function arraysAreEqual(arr1, arr2) {
	if (!arr1 || !arr2) {
		return arr1 === arr2;
	}
	if (arr1.length !== arr2.length) {
		return false;
	}
	return arr1.every((val, index) => val === arr2[index]);
}

/**
 * Performs a deep equality comparison between two objects.
 * Recursively compares all nested properties and values.
 *
 * @param {*} obj1 - The first object to compare
 * @param {*} obj2 - The second object to compare
 * @returns {boolean} True if objects are deeply equal, false otherwise
 *
 * @example
 * deepEqual({ a: 1 }, { a: 2 }); // returns false
 * deepEqual({ a: { b: 1 } }, { a: { b: 1 } }); // returns true
 * deepEqual(null, null); // returns true
 * deepEqual(undefined, null); // returns false
 */
export function deepEqual(obj1, obj2) {
	// Check if both are same reference
	if (obj1 === obj2) {
		return true;
	}

	// Check if either is null or not an object
	if (obj1 == null || obj2 == null) {
		return false;
	}
	if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
		return false;
	}

	// Get keys
	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);

	// Check if same number of keys
	if (keys1.length !== keys2.length) return false;

	// Check each key and value recursively
	for (let key of keys1) {
		if (!keys2.includes(key)) return false;
		if (!deepEqual(obj1[key], obj2[key])) return false;
	}

	return true;
}
